import styled from '@emotion/styled';

import { OutlineButton, PrimaryButton } from '@/components/common/Button/style';

export const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 25;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: ${({ theme }) => theme.scale.spaceLg};
	background: rgba(0, 21, 52, 0.48);
	backdrop-filter: blur(2px);
`;

export const Panel = styled.div<{ $compact?: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: ${({ theme, $compact }) => ($compact ? theme.scale.spaceMd : theme.scale.spaceLg)};
	width: 100%;
	max-width: ${({ $compact }) => ($compact ? '38rem' : '44rem')};
	max-height: min(85vh, 56rem);
	padding: ${({ theme, $compact }) => ($compact ? `${theme.scale.spaceXl} ${theme.scale.spaceLg}` : theme.scale.space2xl)};
	border: 1px solid ${({ theme }) => theme.ui.border.default};
	border-radius: ${({ theme }) => theme.effects.radiusLg};
	background: ${({ theme }) => theme.ui.surface.page};
	box-shadow: ${({ theme }) => theme.effects.shadowLg};
	overflow-y: auto;
	box-sizing: border-box;

	@media screen and (max-width: 480px) {
		padding: ${({ theme }) => theme.scale.spaceLg};
	}
`;

export const Body = styled.div<{ $centered?: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: ${({ $centered }) => ($centered ? 'center' : 'stretch')};
	gap: ${({ theme }) => theme.scale.spaceSm};
	width: 100%;
	text-align: ${({ $centered }) => ($centered ? 'center' : 'inherit')};
`;

export const IconBadge = styled.div<{ $tone?: 'primary' | 'neutral' }>`
	display: flex;
	align-items: center;
	justify-content: center;
	align-self: center;
	width: 5.2rem;
	height: 5.2rem;
	border-radius: ${({ theme }) => theme.effects.radiusFull};
	background: ${({ $tone, theme }) => ($tone === 'neutral' ? theme.ui.surface.subtle : theme.colors.primary50)};
	color: ${({ $tone, theme }) => ($tone === 'neutral' ? theme.ui.text.secondary : theme.colors.primary400)};
	font-size: 2.2rem;
	font-weight: ${({ theme }) => theme.fontWeight.weightBold};
	line-height: 1;
`;

export const BrandLabel = styled.span`
	font-size: ${({ theme }) => theme.fontSize.sizeXs};
	font-weight: ${({ theme }) => theme.fontWeight.weightBold};
	line-height: 1.3;
	letter-spacing: ${({ theme }) => theme.ui.letterSpacing.label};
	text-transform: uppercase;
	color: ${({ theme }) => theme.ui.interactive.primary};
`;

export const Message = styled.p<{ $emphasis?: boolean }>`
	margin: 0;
	padding-top: ${({ $emphasis, theme }) => ($emphasis ? theme.scale.spacexs : 0)};
	font-size: ${({ theme, $emphasis }) => ($emphasis ? theme.fontSize.sizeLg : theme.fontSize.sizeMd)};
	font-weight: ${({ theme, $emphasis }) =>
		$emphasis ? theme.fontWeight.weightSemi : theme.fontWeight.weightRegular};
	line-height: 1.55;
	letter-spacing: ${({ theme }) => theme.ui.letterSpacing.body};
	color: ${({ theme }) => theme.ui.text.primary};
	white-space: pre-line;
	text-align: center;
	word-break: keep-all;
`;

export const Footer = styled.div<{ $stack?: boolean; $fullWidth?: boolean }>`
	display: flex;
	flex-direction: ${({ $stack }) => ($stack ? 'column-reverse' : 'row')};
	justify-content: ${({ $stack, $fullWidth }) => ($fullWidth || $stack ? 'stretch' : 'flex-end')};
	align-items: stretch;
	gap: ${({ theme }) => theme.scale.spaceSm};
	width: 100%;
	margin-top: ${({ theme, $fullWidth }) => ($fullWidth ? theme.scale.spacexs : 0)};

	&[data-stack='true'] > button,
	&[data-full-width='true'] > button {
		width: 100%;
		min-width: 0;
	}
`;

export const DialogPrimaryButton = styled(PrimaryButton)`
	min-width: 10rem;
`;

export const DialogOutlineButton = styled(OutlineButton)`
	min-width: 10rem;
`;
